import type { BoardSummary, Role } from "@mn/shared";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { BoardContext, type BoardCtx } from "@/board/BoardContext";
import { isLight } from "@/board/Canvas";
import { CardView } from "@/board/cards/CardView";
import { type BoardHandle, openBoard } from "@/board/collab";
import { useMapKeys } from "@/board/hooks";
import { Interactions } from "@/board/interactions";
import { Lines } from "@/board/Lines";
import { useLabels } from "@/board/labels";
import { SpatialIndex } from "@/board/spatial";
import { useBoardUI } from "@/board/store";
import { api } from "@/lib/api";

const PAD = 48;
const HEADER = 64;

interface Layout {
  w: number;
  h: number;
  ox: number;
  oy: number;
  scale: number;
}

const mark = (status: string, w = 0, h = 0) => {
  document.body.dataset.printW = String(w);
  document.body.dataset.printH = String(h);
  document.body.dataset.printReady = status;
};

/**
 * Lean, chrome-less rendering of a whole board at zoom 1 for the server-side PDF/PNG exporter.
 * Signals readiness on <body data-print-ready> once the doc is synced and every image has loaded.
 */
export default function PrintPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const maxSide = Number(params.get("max") ?? 14000);
  const lightTheme = params.get("theme") === "light";
  const [ctx, setCtx] = useState<BoardCtx | null>(null);
  const [title, setTitle] = useState("");
  const [background, setBackground] = useState<string | null>(null);
  const [synced, setSynced] = useState(false);
  const [layout, setLayout] = useState<Layout | null>(null);
  const worldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let handle: BoardHandle | null = null;
    let cancelled = false;
    api<{ board: BoardSummary; role: Role }>(`/api/boards/${id}`)
      .then((view) => {
        if (cancelled) return;
        setTitle(view.board.title);
        setBackground(view.board.background);
        const user = { id: "print", name: "print", image: null, color: "#888" };
        handle = openBoard(view.board.id, user, {
          readOnly: true,
          tokenUrl: "/api/share/collab-token",
          onSynced: () => setSynced(true),
          onAuthFailed: () => mark("forbidden"),
        });
        const index = new SpatialIndex(handle.types.cards);
        const interactions = new Interactions(document.body, handle, index, user.id, false);
        useBoardUI.setState({ camera: { x: 0, y: 0, z: 1 }, selection: new Set(), editingId: null });
        setCtx({
          boardId: view.board.id,
          handle,
          index,
          interactions,
          role: "viewer",
          canEdit: false,
          user,
          openBoard: () => {},
        });
      })
      .catch(() => mark("notfound"));
    void useLabels.getState().load();
    return () => {
      cancelled = true;
      handle?.close();
    };
  }, [id]);

  // Once synced: wait for fonts and images, measure the drawn extent, then publish the final size.
  useEffect(() => {
    if (!ctx || !synced || layout) return;
    let alive = true;
    const started = Date.now();
    const settle = async () => {
      await document.fonts.ready.catch(() => {});
      for (;;) {
        if (!alive) return;
        const imgs = [...document.querySelectorAll("img")];
        const pending = imgs.some((i) => !i.complete && i.getAttribute("src"));
        if (!pending && Date.now() - started > 600) break;
        if (Date.now() - started > 20_000) break;
        await new Promise((r) => setTimeout(r, 200));
      }
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (!alive) return;
      const world = worldRef.current;
      if (!world) return;
      const base = world.getBoundingClientRect();
      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;
      for (const el of world.querySelectorAll<HTMLElement>("[data-card]")) {
        if (el.closest("[data-card]") !== el) continue; // children of columns are inside their column
        const r = el.getBoundingClientRect();
        minX = Math.min(minX, r.left - base.left);
        minY = Math.min(minY, r.top - base.top);
        maxX = Math.max(maxX, r.right - base.left);
        maxY = Math.max(maxY, r.bottom - base.top);
      }
      if (!Number.isFinite(minX)) {
        minX = 0;
        minY = 0;
        maxX = 600;
        maxY = 200;
      }
      const w = maxX - minX + PAD * 2;
      const h = maxY - minY + PAD * 2 + HEADER;
      const scale = Math.min(1, maxSide / Math.max(w, h));
      setLayout({
        w: Math.ceil(w * scale),
        h: Math.ceil(h * scale),
        ox: PAD - minX,
        oy: PAD + HEADER - minY,
        scale,
      });
    };
    void settle();
    return () => {
      alive = false;
    };
  }, [ctx, synced, layout, maxSide]);

  useEffect(() => {
    if (!layout) return;
    const t = requestAnimationFrame(() => requestAnimationFrame(() => mark("ok", layout.w, layout.h)));
    return () => cancelAnimationFrame(t);
  }, [layout]);

  if (!ctx) return null;
  return (
    <BoardContext.Provider value={ctx}>
      {lightTheme ? (
        <style>{`[data-card][data-color="transparent"] { color: #111 !important; } [data-card][data-color="transparent"] .text-dim { color: #666 !important; }`}</style>
      ) : null}
      <div
        data-print-root
        className="relative overflow-hidden bg-canvas"
        style={{
          width: layout?.w ?? 20000,
          height: layout?.h ?? 20000,
          backgroundColor: lightTheme ? "#f6f4ef" : (background ?? undefined),
          backgroundImage: `radial-gradient(circle, ${lightTheme || (background && isLight(background)) ? "#c9c6bf" : "#3a3a3a"} 1px, transparent 1.2px)`,
          color: lightTheme ? "#111" : undefined,
          backgroundSize: `${20 * (layout?.scale ?? 1)}px ${20 * (layout?.scale ?? 1)}px`,
        }}
      >
        <div
          className="absolute left-0 top-0"
          style={{ transform: `scale(${layout?.scale ?? 1})`, transformOrigin: "0 0" }}
        >
          <div
            className="absolute flex items-baseline gap-3"
            style={{ left: PAD, top: PAD / 2, whiteSpace: "nowrap" }}
          >
            <span className={`wordmark text-2xl ${lightTheme ? "text-[#111]" : "text-text"}`}>
              .MAYUSCULANOTE
            </span>
            <span className={`text-lg font-semibold ${lightTheme ? "text-[#555]" : "text-muted"}`}>
              {title}
            </span>
          </div>
          <div
            ref={worldRef}
            data-world
            className="absolute left-0 top-0 h-0 w-0"
            style={{ transform: layout ? `translate(${layout.ox}px, ${layout.oy}px)` : undefined }}
          >
            <PrintCards handle={ctx.handle} />
            <Lines />
          </div>
        </div>
      </div>
    </BoardContext.Provider>
  );
}

function PrintCards({ handle }: { handle: BoardHandle }) {
  const ids = useMapKeys(handle.types.cards);
  return (
    <>
      {ids.map((id) => {
        const m = handle.types.cards.get(id);
        return m && !m.get("parent") && !m.get("unsorted") ? <CardView key={id} id={id} ymap={m} /> : null;
      })}
    </>
  );
}
